import { Alert02Icon, AlertCircleIcon, ArrowRight01Icon, CheckmarkCircle01Icon, Mail01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/hooks";
import { isLoggedInSelector, logOut } from "@/features/auth/slice";
import { 
  useFetchInvitationByIdQuery, 
  useAcceptInviteMutation 
} from "@/api/accessApi";
import { auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress, 
  Stack, 
  Alert, 
  Chip,
  Grow
} from "@mui/material";
import routes from "@/constants/routes";

const AccessInvitationPage: React.FC = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const currentUser = useAppSelector((state) => state?.auth?.user);
  
  const [authChecked, setAuthChecked] = useState(false);
  
  const { 
    data: invitation, 
    isLoading: isInvitationLoading, 
    error: invitationError 
  } = useFetchInvitationByIdQuery(invitationId || "", {
    skip: !invitationId,
  });

  const [acceptInvite, { isLoading: isAccepting }] = useAcceptInviteMutation();
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Wait for Firebase auth to initialize
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  // Determine if there is an email mismatch
  const emailMismatch = isLoggedIn && authChecked && invitation && 
    (!currentUser?.email || currentUser.email.trim().toLowerCase() !== invitation.email.trim().toLowerCase());

  // Redirect to dashboard on success after a short delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate(routes.organizations.main);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const handleAccept = async () => {
    if (!invitationId) return;
    setErrorMessage(null);
    try {
      await acceptInvite({ invitationId }).unwrap();
      setSuccess(true);
    } catch (err: any) {
      console.error("Error accepting invitation:", err);
      setErrorMessage(
        err?.data?.statusText || 
        err?.message || 
        "Hubo un problema al procesar la invitación. Por favor intenta de nuevo."
      );
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "admin":
        return "Administrador";
      case "manager":
        return "Gestor";
      case "rider":
        return "Repartidor";
      default:
        return role;
    }
  };

  const handleLoginRedirect = () => {
    navigate(routes.login, { 
      state: { prevPage: location.pathname } 
    });
  };

  const handleSignUpRedirect = () => {
    navigate("/sign-up", { 
      state: { prevPage: location.pathname } 
    });
  };

  // 1. Loading State (Invitation loading or Auth state not yet checked)
  if (isInvitationLoading || !authChecked) {
    return (
      <Box 
        sx={{ 
          minHeight: "100vh", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          backgroundColor: "#F8FAFC",
          p: 3
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={50} sx={{ color: "#015FEE" }} />
          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
            Verificando los detalles de tu invitación...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // 2. Error / Not Found / Not Pending State
  const isInactive = invitation && invitation.status !== "pending";
  const hasError = invitationError || isInactive || !invitation;

  if (hasError) {
    return (
      <Box 
        sx={{ 
          minHeight: "100vh", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          backgroundColor: "#F8FAFC",
          p: 3
        }}
      >
        <Grow in timeout={500}>
          <Paper 
            elevation={0} 
            sx={{ 
              maxWidth: 450, 
              width: "100%", 
              p: 5, 
              borderRadius: "24px", 
              textAlign: "center",
              boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.02)",
              backgroundColor: "#FFFFFF"
            }}
          >
            <Box 
              sx={{ 
                width: 64, 
                height: 64, 
                borderRadius: "50%", 
                backgroundColor: "#FEF2F2", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                margin: "0 auto 24px auto" 
              }}
            >
              <HugeiconsIcon icon={AlertCircleIcon} size={32} color="#EF4444"  />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#0F172A" }}>
              Invitación Inválida
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4, px: 2 }}>
              {isInactive 
                ? "Esta invitación ya ha sido aceptada o cancelada anteriormente." 
                : "No logramos encontrar esta invitación. Por favor solicita una nueva invitación al administrador de la organización."}
            </Typography>
            <Button 
              variant="contained" 
              fullWidth
              onClick={() => navigate("/")}
              sx={{ 
                backgroundColor: "#015FEE",
                "&:hover": {
                  backgroundColor: "#004ac4",
                },
                borderRadius: "12px",
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "0px 4px 12px rgba(1, 95, 238, 0.2)"
              }}
            >
              Ir a Portada
            </Button>
          </Paper>
        </Grow>
      </Box>
    );
  }

  // 2b. Email Mismatch State (Active user email differs from invitation email)
  if (emailMismatch) {
    return (
      <Box 
        sx={{ 
          minHeight: "100vh", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          backgroundColor: "#F8FAFC",
          p: 3
        }}
      >
        <Grow in timeout={500}>
          <Paper 
            elevation={0} 
            sx={{ 
              maxWidth: 480, 
              width: "100%", 
              p: 5, 
              borderRadius: "24px", 
              textAlign: "center",
              boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.02)",
              backgroundColor: "#FFFFFF"
            }}
          >
            <Box 
              sx={{ 
                width: 64, 
                height: 64, 
                borderRadius: "50%", 
                backgroundColor: "#FEF3C7", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                margin: "0 auto 24px auto" 
              }}
            >
              <HugeiconsIcon icon={Alert02Icon} size={32} color="#F59E0B"  />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#0F172A" }}>
              Invitación No Válida
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, px: 2 }}>
              La cuenta con la que iniciaste sesión (<strong>{currentUser?.email || "tu cuenta actual"}</strong>) no coincide con el correo al que se envió esta invitación.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4, px: 2 }}>
              Para poder aceptar esta invitación debes cerrar sesión e iniciar con la cuenta correspondiente.
            </Typography>
            <Stack spacing={2}>
              <Button 
                variant="contained" 
                fullWidth
                onClick={() => {
                  dispatch(logOut());
                }}
                sx={{ 
                  backgroundColor: "#015FEE",
                  "&:hover": {
                    backgroundColor: "#004ac4",
                  },
                  borderRadius: "12px",
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "0px 4px 12px rgba(1, 95, 238, 0.2)"
                }}
              >
                Cerrar Sesión
              </Button>
              <Button 
                variant="outlined" 
                fullWidth
                onClick={() => navigate("/")}
                sx={{ 
                  borderRadius: "12px",
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#E2E8F0",
                  color: "#64748B",
                  "&:hover": {
                    borderColor: "#CBD5E1",
                    backgroundColor: "#F8FAFC"
                  }
                }}
              >
                Ir a Portada
              </Button>
            </Stack>
          </Paper>
        </Grow>
      </Box>
    );
  }

  // 3. Success State (After clicking Accept)
  if (success) {
    return (
      <Box 
        sx={{ 
          minHeight: "100vh", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          backgroundColor: "#F8FAFC",
          p: 3
        }}
      >
        <Grow in timeout={500}>
          <Paper 
            elevation={0} 
            sx={{ 
              maxWidth: 480, 
              width: "100%", 
              p: 5, 
              borderRadius: "24px", 
              textAlign: "center",
              boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.02)",
              backgroundColor: "#FFFFFF"
            }}
          >
            <Box 
              sx={{ 
                width: 64, 
                height: 64, 
                borderRadius: "50%", 
                backgroundColor: "#DCFCE7", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                margin: "0 auto 24px auto" 
              }}
            >
              <HugeiconsIcon icon={CheckmarkCircle01Icon} size={32} color="#22C55E"  />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: "#0F172A" }}>
              ¡Bienvenido!
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#015FEE", mb: 2 }}>
              {invitation.orgName}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
              Has aceptado la invitación con éxito. Redirigiéndote a tu panel de control...
            </Typography>
            <CircularProgress size={30} sx={{ color: "#015FEE" }} />
          </Paper>
        </Grow>
      </Box>
    );
  }

  // 4. Main Acceptance / Auth State View
  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        backgroundColor: "#F8FAFC",
        p: 3
      }}
    >
      <Grow in timeout={500}>
        <Paper 
          elevation={0} 
          sx={{ 
            maxWidth: 480, 
            width: "100%", 
            p: { xs: 4, sm: 5 }, 
            borderRadius: "24px", 
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.02)",
            backgroundColor: "#FFFFFF"
          }}
        >
          {/* Logo / Icon Header */}
          <Stack alignItems="center" spacing={1} sx={{ mb: 4 }}>
            <Box 
              sx={{ 
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#EFF6FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <HugeiconsIcon icon={Mail01Icon} size={24} color="#015FEE"  />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A", textAlign: "center", mt: 1 }}>
              Invitación a Colaborar
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", textAlign: "center" }}>
              Has sido invitado a unirte a un equipo en BiteLink.
            </Typography>
          </Stack>

          {/* Invitation Detail Card */}
          <Box 
            sx={{ 
              p: 3, 
              mb: 4, 
              backgroundColor: "#F8FAFC", 
              borderRadius: "16px",
            }}
          >
            <Stack spacing={2.5}>
              <Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "#94A3B8", 
                    textTransform: "uppercase", 
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem"
                  }}
                >
                  Comercio / Organización
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: "#0F172A", mt: 0.5 }}>
                  {invitation?.orgName}
                </Typography>
              </Box>
              
              <Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "#94A3B8", 
                    textTransform: "uppercase", 
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem"
                  }}
                >
                  Rol Asignado
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label={getRoleLabel(invitation?.role || "")} 
                    sx={{ 
                      fontWeight: 600, 
                      backgroundColor: "#015FEE", 
                      color: "#FFFFFF",
                      borderRadius: "100px",
                      height: 28,
                      fontSize: "0.75rem"
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ borderTop: "1px solid #F1F5F9", pt: 2.5 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "#94A3B8", 
                    textTransform: "uppercase", 
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem"
                  }}
                >
                  Invitado
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: "#0F172A", mt: 0.5 }}>
                  {invitation?.email}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Error Message Alert */}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
              {errorMessage}
            </Alert>
          )}

          {/* Conditional view based on Auth Status */}
          {!isLoggedIn ? (
            <Stack spacing={2.5}>
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: "12px", 
                  alignItems: "center",
                  backgroundColor: "#EFF6FF",
                  color: "#1E40AF",
                  "& .MuiAlert-icon": { color: "#1D4ED8" }
                }}
              >
                Debes iniciar sesión o crear una cuenta de BiteLink para poder aceptar esta invitación.
              </Alert>
              <Button 
                variant="contained" 
                fullWidth
                onClick={handleLoginRedirect}
                sx={{ 
                  backgroundColor: "#015FEE",
                  "&:hover": {
                    backgroundColor: "#004ac4",
                  },
                  borderRadius: "12px",
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  boxShadow: "0px 4px 12px rgba(1, 95, 238, 0.2)"
                }}
              >
                Iniciar Sesión
              </Button>
              <Button 
                variant="outlined" 
                fullWidth
                onClick={handleSignUpRedirect}
                sx={{ 
                  borderRadius: "12px",
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  borderColor: "#E2E8F0",
                  color: "#64748B",
                  "&:hover": {
                    borderColor: "#CBD5E1",
                    backgroundColor: "#F8FAFC"
                  }
                }}
              >
                Crear Cuenta nueva
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Button 
                variant="contained" 
                fullWidth
                onClick={handleAccept}
                disabled={isAccepting}
                endIcon={!isAccepting && <HugeiconsIcon icon={ArrowRight01Icon} />}
                sx={{ 
                  backgroundColor: "#015FEE",
                  "&:hover": {
                    backgroundColor: "#004ac4",
                  },
                  borderRadius: "12px",
                  py: 1.8,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "1rem",
                  boxShadow: "0px 4px 12px rgba(1, 95, 238, 0.2)"
                }}
              >
                {isAccepting ? <CircularProgress size={24} color="inherit" /> : "Aceptar Invitación"}
              </Button>
              
              <Button 
                variant="text" 
                onClick={() => navigate("/")}
                sx={{ 
                  color: "#94A3B8",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  "&:hover": {
                    color: "#64748B",
                    backgroundColor: "transparent",
                    textDecoration: "underline"
                  }
                }}
              >
                Rechazar invitación
              </Button>
            </Stack>
          )}
        </Paper>
      </Grow>
    </Box>
  );
};

export default AccessInvitationPage;
